import { Row } from "@/hooks";
import { Option } from "@/models/class/option.class";
import {
  LeasingSourceEnum,
  LeasingStateEnum,
} from "@/models/enums/Leasing.enum";
import {
  LeasingAssetCreateRequestDto,
  LeasingAssetResponseDto,
  LeasingDetailResponseDto,
  LeasingPeriodCreateRequestDto,
  LeasingPeriodResponseDto,
} from "@/models/interface/leasing";
import { LabelInValue } from "@/types";
import { Moment } from "moment";

export const FORM_MODE = {
  EARLY_SETTLEMENT: "EARLY_SETTLEMENT",
} as const;

export type FormMode = (typeof FORM_MODE)[keyof typeof FORM_MODE];

type AssetItem = Omit<LeasingAssetCreateRequestDto, "dealerId"> &
  Pick<
    LeasingAssetResponseDto,
    | "assetUnitCode"
    | "brand"
    | "type"
    | "provision"
    | "residue"
    | "serialNumber"
    | "nettFinance"
    | "interest"
    | "insurancePremium"
    | "invoiceAPId"
    | "invoiceApNumber"
  > & {
    dealer: LabelInValue | undefined;
    dealerOptions: Option[];
    isSearchDealer: boolean;
    loadingDealer: boolean;
  };

type PeriodActionLoadingState = {
  settle: boolean;
  cancel: boolean;
};

type PeriodItem = Omit<
  LeasingPeriodCreateRequestDto,
  "paymentDate" | "paymentRealizationDate" | "parentId"
> &
  Pick<
    LeasingPeriodResponseDto,
    | "amortize"
    | "balance"
    | "cashInOutDocumentNumber"
    | "cashInOutId"
    | "installment"
    | "paymentCost"
    | "subInstallment"
    | "installmentLabel"
  > & {
    parentId: string; // FE-only: set locally in handleAddRow for new [+] rows
    paymentDate: Moment | null;
    paymentRealizationDate: Moment | null;
    loading: PeriodActionLoadingState;
    notes: string;
  };

type RowAsset = Row<AssetItem, string>;
type RowPeriod = Row<PeriodItem, string>;

type FormValue = {
  assetList: Array<RowAsset>;
  billingAddressLessor: string;
  branch: LabelInValue | undefined;
  cancellable: boolean;
  chequeNumber: string;
  companyBank: LabelInValue | undefined;
  costPrice: number;
  currency: LabelInValue | undefined;
  deletedAssetLineIds: Array<string>;
  deletedPeriodLineIds: Array<string>;
  id: string;
  insurance: LabelInValue | undefined;
  insurancePeriod: number;
  insurancePremium: number;
  interest: number;
  interestCostAccount: LabelInValue | undefined;
  journalId: string | null;
  journalNo: string;
  leasingContractDate: Moment | null;
  leasingContractNumber: string;
  leasingNumber: string;
  leasingPeriod: number;
  lessor: LabelInValue | undefined;
  nettFinance: number;
  notes: string;
  paidOff: boolean;
  paidOffDate: Moment | null;
  periodList: Array<RowPeriod>;
  policyNumber: string;
  principalCostAccount: LabelInValue | undefined;
  provision: number;
  rates: number;
  residue: number;
  startPaymentDate: Moment | null;
  status: LeasingStateEnum;
  taxTotalAmount: number;
  total: number;
  useCheque: boolean;
  source: LeasingSourceEnum | undefined;
  invoices: LabelInValue[];
  otr: number;
};

type LeasingInvoiceState = {
  invoiceId: string;
  otr: number;
  invoiceDate: number; // epoch ms
};

type State = {
  form: FormValue;
  detail: LeasingDetailResponseDto;
  generatedInvoices: LeasingInvoiceState[];
  maxInvoiceDate: number | null;
};

export {
  AssetItem,
  FormValue,
  LeasingInvoiceState,
  PeriodActionLoadingState,
  PeriodItem,
  RowAsset,
  RowPeriod,
  State,
};
